import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { cleanupOnLogout, isAuthenticatedHook } from "@src/utils/auth";
import { useAuthState } from "@src/hooks/useAuth";
import { useNotification } from "@src/context/NotificationContext";
import LoadingOverlay from "@components/LoadingOverlay";
import NoPermissionPage from "@src/pages/NoPermission/NoPermissionPage";

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminRoute?: boolean;
  adminOnlyRoute?: boolean;
  articleRoute?: boolean;
  curatorOnlyRoute?: boolean;
  /** Admins and curators only — e.g. invoices, which Strapi doesn't grant managers. */
  noManagerRoute?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = React.memo(
  ({
    children,
    adminRoute,
    adminOnlyRoute,
    articleRoute,
    curatorOnlyRoute,
    noManagerRoute,
  }) => {
    const { isAuthenticated, loading, role, curator } = useAuthState();
    const location = useLocation();
    const checkAuth = isAuthenticatedHook();
    const { addNotification } = useNotification();
    useEffect(() => {
      if (loading) return;
      if (
        role?.type !== "admin" &&
        role?.type !== "curator" &&
        role?.type !== "manager"
      ) {
        addNotification(
          "You do not have permission to access this dashboard",
          "error"
        );
        cleanupOnLogout();
      }
      if (isAuthenticated && !checkAuth) {
        cleanupOnLogout();
      }
    }, [isAuthenticated, checkAuth, loading]);

    // Hold the route back until the role resolves. Rendering the children first
    // flashes a page the signed-in role may not be allowed to see [ROVR-3406].
    if (loading) return <LoadingOverlay />;

    const regularAuthPermission = isAuthenticated && checkAuth && !loading;
    const curatorPermission = role?.type === "curator" && regularAuthPermission;
    const adminPermission = role?.type === "admin" && regularAuthPermission;
    const managerPermission = role?.type === "manager" && regularAuthPermission;
    const canAccessArticles =
      adminPermission || managerPermission || Boolean(curator?.articleEditor);

    if (!regularAuthPermission) {
      // Carry the requested route through the login screen so a deep link — e.g. the
      // "View Message" button in the curator new-message email — still lands on it.
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: `${location.pathname}${location.search}` }}
        />
      );
    }

    // A signed-in user who may not open this page is told so rather than
    // silently sent home, so a link or a typed URL doesn't look broken.
    if (articleRoute && !canAccessArticles) {
      return <NoPermissionPage />;
    }

    if (curatorOnlyRoute && !curatorPermission) {
      return <NoPermissionPage />;
    }

    if (noManagerRoute && managerPermission) {
      return <NoPermissionPage />;
    }

    if (adminOnlyRoute) {
      return adminPermission ? <>{children}</> : <NoPermissionPage />;
    }

    if (curatorPermission && !adminRoute) {
      return <>{children}</>;
    }
    if (adminPermission) {
      return <>{children}</>;
    }
    if (managerPermission) {
      return <>{children}</>;
    }
    return <NoPermissionPage />;
  }
);

export default ProtectedRoute;
